"use client";

import { useState, useEffect } from "react";
import { User, Settings, LogOut, Package, Loader2, Mail } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth/auth-context";
import { InvitationBadge } from "./invitation-badge";

export function UserMenu() {
  const { isAuthenticated, user, isLoading, login, logout, pendingInvitationsCount } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Before mount: render a stable placeholder to avoid hydration mismatch
  if (!mounted || isLoading) {
    return (
      <div className="hidden lg:flex items-center gap-2">
        <div className="h-9 w-9 rounded-full" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="hidden lg:flex items-center gap-2">
        <Button variant="secondary" size="sm" className="rounded-full px-4 hover:bg-muted-foreground/20" onClick={() => login()}>
          Войти
        </Button>
      </div>
    );
  }

  // Authenticated
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user.avatarUrl ?? undefined} alt={user.name} />
            <AvatarFallback>
              {user.name?.substring(0, 2).toUpperCase() || "US"}
            </AvatarFallback>
          </Avatar>
          <InvitationBadge />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-2">
        <DropdownMenuLabel className="px-2 py-2">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="-mx-2 my-2" />
        <DropdownMenuItem asChild>
          <Link href="/profile">
            <User className="mr-2 h-4 w-4" />
            Профиль
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/my-products">
            <Package className="mr-2 h-4 w-4" />
            Мои продукты
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/invitations">
            <Mail className="mr-2 h-4 w-4" />
            <span className="flex-1">Приглашения</span>
            {pendingInvitationsCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {pendingInvitationsCount}
              </Badge>
            )}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/settings">
            <Settings className="mr-2 h-4 w-4" />
            Настройки
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="-mx-2 my-2" />
        <DropdownMenuItem
          className="text-destructive"
          onClick={() => logout()}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
